import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ImageList,
  ImageListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { DataNode, NodeDataType } from "graphql/types"

type CriteriaInfoDialogProps = {
  open: boolean
  onClose: () => void
  criteriaName: string
  description: DataNode[]
}

export const CriteriaInfoDialog = ({
  open,
  onClose,
  criteriaName,
  description,
}: CriteriaInfoDialogProps) => {
  const renderDataNode = (node: DataNode, index: number) => {
    switch (node.dataType) {
      case NodeDataType.TEXT:
        return (
          <Typography key={node.id || index} variant="body2" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
            {node.text}
          </Typography>
        )

      case NodeDataType.TABLE:
        return (
          <Box key={node.id || index} sx={{ mb: 2 }}>
            {node.tableCaption && (
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                {node.tableCaption}
              </Typography>
            )}
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                {node.tableHeader.length > 0 && (
                  <TableHead>
                    <TableRow>
                      {node.tableHeader.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          colSpan={cell.colSpan}
                          sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}
                        >
                          {cell.data}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {node.tableBody.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex} colSpan={cell.colSpan}>
                          {cell.data}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )

      case NodeDataType.IMAGE:
        return (
          <Box key={node.id || index} sx={{ mb: 2 }}>
            {node.imageNumber.length > 0 && (
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                Abbildung {node.imageNumber.join(", ")}
              </Typography>
            )}
            {node.image.length > 0 && (
              <ImageList cols={1} sx={{ maxHeight: 400 }}>
                {node.image.map((imageUrl, imageIndex) => (
                  <ImageListItem key={imageIndex}>
                    <img
                      src={imageUrl}
                      alt={node.imageCaption[imageIndex] || `Abbildung ${imageIndex + 1}`}
                      loading="lazy"
                      style={{ maxHeight: "300px", objectFit: "contain" }}
                    />
                    {node.imageCaption[imageIndex] && (
                      <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                        {node.imageCaption[imageIndex]}
                      </Typography>
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{criteriaName}</Typography>
      </DialogTitle>

      <DialogContent>
        {description.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Keine zusätzlichen Informationen verfügbar.
          </Typography>
        ) : (
          <Box>{description.map((node, index) => renderDataNode(node, index))}</Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  )
}
